import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Server, Mic, Sparkles, Leaf } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { useLanguage } from '@/context/LanguageContext';

const ICONS = { server: Server, mic: Mic, sparkles: Sparkles, leaf: Leaf };

/**
 * SupportTransparencyBlock — shows month-to-date donation total + per-category
 * breakdown so donors can see where their money goes.
 *
 * Backed by GET /api/support-transparency (public). When Stripe is wired,
 * `live=true` and numbers update automatically; otherwise placeholder copy.
 */
const SupportTransparencyBlock = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: resp } = await apiFetch('/api/support-transparency');
      if (!cancelled && resp) setData(resp);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const raised = Number(data.raised_usd || 0);
  const goal = Number(data.goal_usd || 300);
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;

  return (
    <Card className="mt-6 border-[#00A896]/30" data-testid="support-transparency-block">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span>
            {isSpanish ? 'Transparencia' : 'Where your $5 went'}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              · {data.month_label}
            </span>
          </span>
          <span className="text-sm font-mono" data-testid="support-transparency-progress-label">
            ${raised.toFixed(2)} / ${goal}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress
          value={pct}
          className="h-2"
          data-testid="support-transparency-progress"
        />
        <div className="grid grid-cols-2 gap-2" data-testid="support-transparency-categories">
          {(data.categories || []).map((c) => {
            const Icon = ICONS[c.icon] || Sparkles;
            return (
              <div
                key={c.label}
                className="rounded-lg border bg-muted/30 px-3 py-2 flex items-center gap-2"
                data-testid={`support-transparency-cat-${c.icon}`}
              >
                <Icon className="h-4 w-4 text-[#00A896] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-muted-foreground truncate">{c.label}</div>
                  <div className="text-sm font-semibold">${Number(c.amount || 0).toFixed(2)}</div>
                </div>
              </div>
            );
          })}
        </div>
        {data.message && (
          <p className="text-[11px] text-muted-foreground italic" data-testid="support-transparency-message">
            {data.message}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SupportTransparencyBlock;
